import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Import games
import MemoryGame from '../games/MemoryGame';
import PuzzleGame from '../games/PuzzleGame';
import LogicGame from '../games/LogicGame';
import PatternGame from '../games/PatternGame';
import SpeedGame from '../games/SpeedGame';
import DinoGame from '../games/DinoGame';
import SnakeGame from '../games/SnakeGame';
import TetrisGame from '../games/TetrisGame';
import Game2048 from '../games/Game2048';
import ColorMatchGame from '../games/ColorMatchGame';
import WordSearchGame from '../games/WordSearchGame';
import MathGame from '../games/MathGame';
import ReactionGame from '../games/ReactionGame';
import RhythmGame from '../games/RhythmGame';
import MazeGame from '../games/MazeGame';
import { recordRecentGame } from '../lib/recentGames';
import { setupGameStatsBridge, listenGameScoreEvents } from '../lib/gameStats';

const gameTitleKeys = {
  memory: 'games.memory',
  puzzle: 'games.puzzle',
  logic: 'games.logic',
  pattern: 'games.pattern',
  speed: 'games.speed',
  dino: 'games.dino',
  snake: 'games.snake',
  tetris: 'games.tetris',
  game2048: 'games.game2048',
  colormatch: 'games.colormatch',
  wordsearch: 'games.wordsearch',
  math: 'games.math',
  reaction: 'games.reaction',
  rhythm: 'games.rhythm',
  maze: 'games.maze'
};

const GamePage = () => {
  const { gameType } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  console.log('GamePage rendered with gameType:', gameType);

  useEffect(() => {
    if (gameType && gameTitleKeys[gameType]) {
      recordRecentGame(gameType, t(gameTitleKeys[gameType]));
    }
  }, [gameType, t]);

  useEffect(() => {
    if (!gameType || !gameTitleKeys[gameType]) return undefined;
    const cleanupBridge = setupGameStatsBridge(gameType);
    const cleanupEvents = listenGameScoreEvents(gameType);
    return () => {
      cleanupBridge();
      cleanupEvents();
    };
  }, [gameType]);

  const gameComponents = {
    memory: MemoryGame,
    puzzle: PuzzleGame,
    logic: LogicGame,
    pattern: PatternGame,
    speed: SpeedGame,
    dino: DinoGame,
    snake: SnakeGame,
    tetris: TetrisGame,
    game2048: Game2048,
    colormatch: ColorMatchGame,
    wordsearch: WordSearchGame,
    math: MathGame,
    reaction: ReactionGame,
    rhythm: RhythmGame,
    maze: MazeGame
  };

  const GameComponent = gameComponents[gameType];

  if (!GameComponent) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-cyber text-neon-red mb-4">{t('game.notFound')}: {gameType}</h1>
          <p className="text-gray-400 mb-4">{t('game.available')}: {Object.keys(gameComponents).join(', ')}</p>
          <button
            onClick={() => navigate('/')}
            className="cyber-button"
          >
            <Home className="h-5 w-5 mr-2" />
            {t('game.backToHome')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16 bg-dark-300">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-dark-100/80 backdrop-blur-sm border-b border-neon-blue/30"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="cyber-button text-sm px-4 py-2 inline-flex items-center space-x-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>{t('game.backToGames')}</span>
            </button>
            
            <h1 className="text-2xl font-cyber font-bold neon-text capitalize">
              {t('game.game')} {gameType}
            </h1>
            
            <div className="w-24"></div> {/* Spacer for centering */}
          </div>
        </div>
      </motion.div>

      {/* Game Container */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
      >
        <div className="text-center mb-4">
          <p className="text-gray-400">{t('game.loading')} {gameType} {t('game.game')}...</p>
        </div>
        
        {/* Game Component with Error Boundary */}
        <div className="game-container">
          <GameComponent />
        </div>
      </motion.div>
    </div>
  );
};

export default GamePage;
